import { isProfileLocked } from "@/utils/profileLock";
import type { SlimProfile } from "@mm/metx-workbench.meteomatics.com";

// Locked (shared) profiles cannot persist `tab_active` on the backend, so each viewer's
// last opened tab is remembered in the browser, keyed by user and profile.
const storageKey = (userId: number, profileId: number) => `mm.lastActiveTab.${userId}.${profileId}`;

export function getLastActiveTab(userId: number | undefined, profileId: number): number | null {
  if (userId == null) {
    return null;
  }
  try {
    const raw = localStorage.getItem(storageKey(userId, profileId));
    const tabId = raw == null ? Number.NaN : Number.parseInt(raw, 10);
    return Number.isNaN(tabId) ? null : tabId;
  } catch {
    return null;
  }
}

export function setLastActiveTab(userId: number | undefined, profileId: number, tabId: number) {
  if (userId == null) {
    return;
  }
  try {
    localStorage.setItem(storageKey(userId, profileId), String(tabId));
  } catch {
    // no-op
  }
}

/**
 * The tab a profile should open on. For locked profiles the viewer's own last tab wins
 * (if it still exists), otherwise `tab_active`, otherwise the first tab.
 */
export function resolveActiveTab(
  profile: Pick<SlimProfile, "id" | "tab_active" | "tabs">,
  userId: number | undefined,
): number | null {
  if (isProfileLocked(profile)) {
    const lastTabId = getLastActiveTab(userId, profile.id);
    if (lastTabId != null && profile.tabs?.some((tab) => tab.id === lastTabId)) {
      return lastTabId;
    }
  }
  return profile.tab_active || profile.tabs?.[0]?.id || null;
}
